// Consented pose-stream recorder. Two ways to switch on:
//   1. Player opt-in ("help improve" card) — anonymous, unlabeled, tagged
//      only with the current mode. Off by default, persisted in localStorage.
//   2. ?capture=1 — a deliberate labeled session (CaptureSession.tsx drives
//      the phase labels) that ALSO downloads the raw trace locally for the
//      QA fake-pose injector.
// What leaves the device: the same flat landmark arrays Unity eats
// ([idx,x,y,z,vis] per joint) — never video, never identity. Chunks are
// gzipped (CompressionStream) and ride the existing insert-only Supabase
// events table as event='pose_chunk' rows, separate queue from telemetry.ts
// so a fat chunk can never crowd out game events.

const SUPABASE_URL = "https://icmvanpbhyxeqtfyfzyz.supabase.co";
const SUPABASE_KEY = "sb_publishable_yZqzVJYS331NOJ4SOY0A8A_QSmDH3SL";
const ENDPOINT = `${SUPABASE_URL}/rest/v1/events`;

const OPTIN_KEY = "aots_pose_share";
const CHUNK_FRAMES = 300;        // ~10s at 30fps
const MAX_CHUNKS = 45;           // ~7min ceiling per session
const FRAME_MIN_GAP_MS = 25;     // cap effective rate at ~40fps

const recSessionId = Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
const device = /iPhone|iPad|Android/i.test(navigator.userAgent) ? "mobile" : "desktop";

export const isCapture = new URLSearchParams(window.location.search).has("capture");

let optIn = false;
try { optIn = localStorage.getItem(OPTIN_KEY) === "1"; } catch { /* private mode */ }

let currentLabel = "";           // capture-session phase ("jog", "squat"...)
let currentMode: string | null = null;
// Opt-in path records ONLY during live gameplay (run_start..run_end/abort
// from Unity telemetry) — never while the player browses menus. Capture
// mode (?capture=1) is exempt: the whole cued session is deliberate.
let gameplayLive = false;
let liveSince = 0;
const MAX_LIVE_MS = 12 * 60 * 1000;   // missed end event safety cap
let frames: number[][] = [];     // [tMs, ...packet]
let allFrames: number[][] = [];  // capture mode keeps everything for download
let seq = 0;
let sentChunks = 0;
let muted = false;
let t0 = 0;
let lastT = 0;

export function getPoseOptIn(): boolean { return optIn; }
export function setPoseOptIn(on: boolean) {
  optIn = on;
  try { localStorage.setItem(OPTIN_KEY, on ? "1" : "0"); } catch { /* ok */ }
  if (!on) { frames = []; }
}
export function setCaptureLabel(label: string) {
  // phase boundary: flush what belongs to the old label first
  if (frames.length) void shipChunk();
  currentLabel = label;
}

function active(): boolean {
  if (muted) return false;
  if (isCapture) return true;
  if (!optIn || !gameplayLive) return false;
  if (performance.now() - liveSince > MAX_LIVE_MS) {
    gameplayLive = false;
    if (frames.length) void shipChunk();
    return false;
  }
  return true;
}

async function gzipB64(s: string): Promise<{ enc: string; b: string }> {
  const CS = (window as any).CompressionStream;
  if (!CS) return { enc: "plain", b: s };
  const stream = new Blob([s]).stream().pipeThrough(new CS("gzip"));
  const buf = await new Response(stream).arrayBuffer();
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000)
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000) as any);
  return { enc: "gzip", b: btoa(bin) };
}

// Camera context (Joshua 2026-08-29): the same move reads differently at a
// different camera pitch/framing, so every chunk records how it was seen.
// Phones report real tilt (packet index 37 rides in-stream too); webcams
// have no sensor, so framing stats derived from the batch itself stand in:
// where the hips sit in frame and the head-to-hip span proxy camera
// height/angle/distance on any device.
function camContext(batch: number[][]) {
  const hipYs: number[] = [], spans: number[] = [];
  for (const f of batch) {
    // frame = [t, idx,x,y,z,vis, ...]; find image-space centers 34 (hip) & 35 (head)
    let hipY: number | null = null, headY: number | null = null;
    for (let i = 1; i + 4 < f.length; i += 5) {
      if (f[i] === 34) hipY = f[i + 2];
      else if (f[i] === 35) headY = f[i + 2];
    }
    if (hipY !== null) hipYs.push(hipY);
    if (hipY !== null && headY !== null) spans.push(Math.abs(hipY - headY));
  }
  const med = (a: number[]) => a.length ? a.sort((x, y) => x - y)[a.length >> 1] : null;
  let orient = "";
  try { orient = window.screen?.orientation?.type || ""; } catch { /* ok */ }
  return {
    hipY: med(hipYs),          // 0 top - 1 bottom of frame
    span: med(spans),          // head-to-hip fraction of frame height
    orient,
    vw: window.innerWidth, vh: window.innerHeight,
    dpr: window.devicePixelRatio || 1,
    ua: navigator.userAgent.slice(0, 120),   // device model/OS, coarse `device` alone hides
  };
}

async function shipChunk() {
  const batch = frames;
  frames = [];
  if (!batch.length || sentChunks >= MAX_CHUNKS) return;
  const mySeq = seq++;
  const label = currentLabel;
  try {
    const { enc, b } = await gzipB64(JSON.stringify(batch));
    const row = {
      session_id: recSessionId,
      build: (window as any).AOTS_BUILD || "unknown",
      device,
      mode: currentMode,
      event: "pose_chunk",
      data: { seq: mySeq, label, n: batch.length, cam: camContext(batch), enc, b },
    };
    const res = await fetch(ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_KEY,
        Authorization: `Bearer ${SUPABASE_KEY}`,
        Prefer: "return=minimal",
      },
      body: JSON.stringify([row]),
    });
    if (res.ok) sentChunks++;
    else muted = true;           // one refusal = stop trying this session
  } catch { muted = true; }
}

// Capture mode: hand the whole labeled trace to the tester as a file the
// injector can replay. Labels ride as [-1, phaseIndex] marker frames.
const phaseMarks: [number, string][] = [];
export function downloadCaptureTrace() {
  const out = {
    sid: recSessionId,
    build: (window as any).AOTS_BUILD || "unknown",
    device,
    phases: phaseMarks,
    cam: camContext(allFrames),
    frames: allFrames,
  };
  const a = document.createElement("a");
  a.download = `pose-capture-${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.json`;
  a.href = URL.createObjectURL(new Blob([JSON.stringify(out)], { type: "application/json" }));
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function getCaptureData() {
  return { frames: allFrames, phases: phaseMarks };
}

/// End-of-capture keypoint graph (PNG data URL) rides up as its own event
export function uploadCaptureSummary(png: string) {
  void fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_KEY,
      Authorization: `Bearer ${SUPABASE_KEY}`,
      Prefer: "return=minimal",
    },
    body: JSON.stringify([{
      session_id: recSessionId,
      build: (window as any).AOTS_BUILD || "unknown",
      device,
      mode: null,
      event: "capture_summary",
      data: { png, cam: camContext(allFrames) },
    }]),
  }).catch(() => { /* best effort */ });
}

export function initPoseRecorder() {
  window.addEventListener("poseData", ((e: CustomEvent) => {
    if (!active()) return;
    const d = e.detail as number[];
    if (!d || !d.length) return;
    const now = performance.now();
    if (now - lastT < FRAME_MIN_GAP_MS) return;
    lastT = now;
    if (!t0) t0 = now;
    const t = Math.round(now - t0);
    // 4-decimal quantization: identical to the eye, gzips far smaller
    const q = d.map((v, i) => (i % 5 === 0 ? v : Math.round(v * 10000) / 10000));
    const frame = [t, ...q];
    frames.push(frame);
    if (isCapture) {
      if (phaseMarks.length === 0 || phaseMarks[phaseMarks.length - 1][1] !== currentLabel)
        phaseMarks.push([t, currentLabel]);
      allFrames.push(frame);
    }
    if (frames.length >= CHUNK_FRAMES) void shipChunk();
  }) as EventListener);

  // mode tag so anonymous data still says what the player was doing
  window.addEventListener("modeEvent", ((e: CustomEvent) => {
    currentMode = String(e.detail?.mode ?? "");
  }) as EventListener);

  // gameplay gate: Unity's own telemetry events bracket every run
  window.addEventListener("telemetry", ((e: CustomEvent) => {
    try {
      const j = JSON.parse(e.detail.j);
      if (j.event === "run_start") {
        gameplayLive = true;
        liveSince = performance.now();
        // run_start's mode is authoritative - it fires for every run,
        // including quest segments (modeEvent only covers the card flow)
        if (j.mode !== undefined) currentMode = String(j.mode);
      }
      else if (j.event === "run_end" || j.event === "run_abort") {
        gameplayLive = false;
        if (frames.length) void shipChunk();
      }
    } catch { /* malformed — ignore */ }
  }) as EventListener);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden" && frames.length) void shipChunk();
  });
}
